import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Lock, CreditCard, ArrowRight, Star } from 'lucide-react';
import { trackPixelEvent } from './MetaPixel';
const CHECKOUT_BASE = 'https://checkout.atlasclinico.com/cart/45629975920742:1';
const CHECKOUT_UPSELL = 'https://checkout.atlasclinico.com/cart/45629984276582:1';
// Fire Meta Pixel event without blocking navigation
function handleCheckoutClick() {
  try {
    trackPixelEvent('InitiateCheckout');
  } catch (_) {
    /* never block the redirect */
  }
}
export function PricingOptions() {
  // Warm up DNS + TLS to checkout origin so the redirect is instant on tap
  useEffect(() => {
    const head = document.head;
    const links: HTMLLinkElement[] = [];
    const add = (rel: string, href: string, crossOrigin?: boolean) => {
      const l = document.createElement('link');
      l.rel = rel;
      l.href = href;
      if (crossOrigin) l.crossOrigin = 'anonymous';
      head.appendChild(l);
      links.push(l);
    };
    add('preconnect', 'https://checkout.atlasclinico.com', true);
    add('dns-prefetch', 'https://checkout.atlasclinico.com');
    return () => {
      links.forEach(l => {
        if (l.parentNode) l.parentNode.removeChild(l);
      });
    };
  }, []);
  return <section id="pricing" className="py-8 md:py-20 lg:py-24 bg-clinical-deep text-white relative overflow-hidden" data-id="element-299">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" data-id="element-300">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, #ffffff 1.5px, transparent 1.5px)',
        backgroundSize: '32px 32px'
      }} data-id="element-301" />
      </div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-clinical-mid/10 rounded-full blur-[120px] pointer-events-none" data-id="element-302"></div>

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-303">
        <div className="text-center max-w-3xl mx-auto mb-6 md:mb-14" data-id="element-304">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-accent mb-3" data-id="element-305">
            Tu inversión
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-2 md:mb-4 leading-[1.05] uppercase" data-id="element-306">
            Elegí tu{' '}
            <span className="accent-italic-gold normal-case" data-id="element-307">Mega Pack</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-sm md:text-lg text-white/70" data-id="element-308">
            Dos formas de entrar al sistema.{' '}
            <span className="accent-italic-gold" data-id="element-309">Una sola decisión.</span>
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto items-stretch" data-id="element-310">
          {/* CARD 1 — Plan Inicial (Gold-themed) */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative bg-[#0A1F38] rounded-2xl md:rounded-3xl p-5 md:p-8 border border-clinical-accent/30 flex flex-col h-full mt-4 md:mt-6" data-id="element-311">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-clinical-accent text-clinical-deep text-[9px] md:text-[10px] font-bold uppercase tracking-[0.22em] py-1 md:py-1.5 px-3 md:px-4 rounded-full whitespace-nowrap flex items-center gap-1.5" data-id="element-312">
              <Star size={10} className="fill-clinical-deep" strokeWidth={0} data-id="element-313" />
              Plan Inicial
            </div>

            <div className="mt-1 md:mt-2" data-id="element-314">
              <h4 className="text-base md:text-2xl font-black text-white mb-1.5 md:mb-2 uppercase leading-tight" data-id="element-315">
                <span className="mr-1.5" data-id="element-316">📘</span>Mega Pack Enfermería
              </h4>
              <p className="text-clinical-accent/80 text-xs md:text-base mb-3 md:mb-6 font-medium" data-id="element-317">
                6 Tomos + 6 Bonos
              </p>
            </div>

            <div className="mb-3 md:mb-7" data-id="element-318">
              <div className="text-clinical-accent/40 line-through text-xs md:text-base font-medium mb-0.5 md:mb-1" data-id="element-319">
                $51.000
              </div>
              <div className="flex items-baseline gap-2" data-id="element-320">
                <span className="text-3xl md:text-5xl font-black text-white" data-id="element-321">
                  $19.990
                </span>
                <span className="text-xs md:text-base font-semibold text-white/50" data-id="element-322">
                  ARS
                </span>
              </div>
            </div>

            <ul className="space-y-2.5 md:space-y-4 mb-4 md:mb-8 flex-grow" data-id="element-323">
              {['Acceso a los 6 Tomos completos', 'Los 6 Bonos de regalo', 'Actualizaciones de por vida'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5 md:gap-3" data-id="element-324">
                  <div className="w-5 h-5 md:w-6 md:h-6 rounded-full border border-clinical-accent/40 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-325">
                    <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-clinical-accent" strokeWidth={3} data-id="element-326" />
                  </div>
                  <span className="text-white/90 text-xs md:text-base leading-snug" data-id="element-327">
                    {item}
                  </span>
                </li>)}
            </ul>

            <a href={CHECKOUT_BASE} target="_top" onClick={handleCheckoutClick} className="w-full py-3 md:py-4 rounded-full border border-clinical-accent/40 text-clinical-accent font-bold text-xs md:text-base hover:bg-clinical-accent/10 active:scale-[0.98] transition-all mt-auto uppercase tracking-wider flex items-center justify-center gap-2 touch-manipulation" data-id="element-328">
              <ArrowRight size={14} data-id="element-329" />
              Quiero mi Mega Pack
            </a>
          </motion.div>

          {/* CARD 2 — Plan Avanzado (Green-themed, highlighted) */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative bg-[#0A1F38] rounded-2xl md:rounded-3xl p-5 md:p-8 border-2 border-emerald-400 shadow-[0_20px_50px_rgba(16,185,129,0.15)] flex flex-col h-full mt-4 md:mt-6" data-id="element-330">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-400 text-clinical-deep text-[9px] md:text-[10px] font-bold uppercase tracking-[0.22em] py-1 md:py-1.5 px-3 md:px-4 rounded-full whitespace-nowrap flex items-center gap-1.5" data-id="element-331">
              <Star size={10} className="fill-clinical-deep" strokeWidth={0} data-id="element-332" />
              Más elegido
            </div>

            <div className="mt-1 md:mt-2" data-id="element-333">
              <h4 className="text-base md:text-2xl font-black text-white mb-1.5 md:mb-2 uppercase leading-tight" data-id="element-334">
                <span className="mr-1.5" data-id="element-335">🏆</span>Mega Pack + Avanzado
              </h4>
              <p className="text-emerald-300/90 text-xs md:text-base mb-3 md:mb-6 font-medium" data-id="element-336">
                6 Tomos + 6 Bonos + 30 casos clínicos resueltos
              </p>
            </div>

            <div className="mb-3 md:mb-7" data-id="element-337">
              <div className="text-emerald-300/40 line-through text-xs md:text-base font-medium mb-0.5 md:mb-1" data-id="element-338">
                $69.000
              </div>
              <div className="flex items-baseline gap-2" data-id="element-339">
                <span className="text-3xl md:text-5xl font-black text-white" data-id="element-340">
                  $27.990
                </span>
                <span className="text-xs md:text-base font-semibold text-white/50" data-id="element-341">
                  ARS
                </span>
              </div>
            </div>

            <ul className="space-y-2.5 md:space-y-4 mb-4 md:mb-8 flex-grow" data-id="element-342">
              {[{
              text: 'Incluye todo lo del Mega Pack básico',
              highlight: false
            }, {
              text: '30 casos clínicos resueltos paso a paso',
              highlight: true
            }, {
              text: 'Casos organizados por área y complejidad',
              highlight: false
            }, {
              text: 'Ideal para prácticas, exámenes e internado',
              highlight: false
            }].map((item, idx) => <li key={idx} className={`flex items-start gap-2.5 md:gap-3 ${item.highlight ? '-mx-1.5 md:-mx-2 px-1.5 md:px-2 py-1.5 md:py-2 rounded-xl bg-emerald-400/10 border border-emerald-400/30' : ''}`} data-id="element-343">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.highlight ? 'bg-emerald-400' : 'border border-emerald-400/40'}`} data-id="element-344">
                    <Check className={`w-3 h-3 md:w-3.5 md:h-3.5 ${item.highlight ? 'text-clinical-deep' : 'text-emerald-400'}`} strokeWidth={3} data-id="element-345" />
                  </div>
                  <span className={`text-xs md:text-base leading-snug ${item.highlight ? 'font-bold text-white' : 'text-white/90'}`} data-id="element-346">
                    {item.text}
                  </span>
                </li>)}
            </ul>

            <a href={CHECKOUT_UPSELL} target="_top" onClick={handleCheckoutClick} className="w-full py-3 md:py-4 rounded-full bg-emerald-400 text-clinical-deep font-bold text-xs md:text-base hover:bg-emerald-300 active:scale-[0.98] transition-all mt-auto uppercase tracking-wider flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(16,185,129,0.3)] touch-manipulation" data-id="element-347">
              <ArrowRight size={14} data-id="element-348" />
              Quiero el Pack + Avanzado
            </a>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-6 md:mt-12 flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 text-[11px] sm:text-sm text-white/60 max-w-3xl mx-auto text-center" data-id="element-349">
          <div className="flex items-center gap-1.5" data-id="element-350">
            <ShieldCheck size={13} className="text-white/50" data-id="element-351" />
            <span data-id="element-352">Garantía 7 días</span>
          </div>
          <span className="text-white/20" data-id="element-353">·</span>
          <div className="flex items-center gap-1.5" data-id="element-354">
            <Lock size={13} className="text-white/50" data-id="element-355" />
            <span data-id="element-356">Pago seguro</span>
          </div>
          <span className="text-white/20" data-id="element-357">·</span>
          <div className="flex items-center gap-1.5" data-id="element-358">
            <CreditCard size={13} className="text-white/50" data-id="element-359" />
            <span data-id="element-360">Mercado Pago · Tarjeta</span>
          </div>
        </motion.div>
      </div>
    </section>;
}